import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPost } from "../api";
import type { CursorPage, Datified, Schemas } from "../../api/types";
import { POLL_BASE_MS } from "./intervals";

export type Suppression = Datified<Schemas["Suppression"], "created_at">;
export type SuppressionCreate = Schemas["SuppressionCreate"];
export type SuppressionReason = Schemas["Suppression"]["reason"];
export type SuppressionScope = Schemas["Suppression"]["scope"];

export interface SuppressionListFilters {
  reason?: string;
  /** Suppressions that apply to this sender: its own and the every-sender ones. */
  sender?: string;
  q?: string;
  cursor?: string;
  limit?: number;
}

export function useSuppressionList(filters: SuppressionListFilters = {}) {
  const u = new URLSearchParams();
  if (filters.reason) u.set("reason", filters.reason);
  if (filters.sender) u.set("sender", filters.sender);
  if (filters.q) u.set("q", filters.q);
  if (filters.cursor) u.set("cursor", filters.cursor);
  if (filters.limit) u.set("limit", String(filters.limit));
  const s = u.toString();
  return useQuery<CursorPage<Suppression>>({
    queryKey: ["suppressions", filters],
    queryFn: () =>
      apiGet<CursorPage<Suppression>>("/suppressions" + (s ? "?" + s : "")),
    refetchInterval: POLL_BASE_MS,
  });
}

export function useCreateSuppression() {
  const qc = useQueryClient();
  return useMutation<Suppression, unknown, SuppressionCreate>({
    meta: { suppressErrorToast: true },
    mutationFn: (input) => apiPost<Suppression>("/suppressions", input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["suppressions"] }),
  });
}

export interface SuppressionTarget {
  channel: string;
  address: string;
  /** null: the suppression that applies to every sender. */
  sender: string | null;
}

export function useDeleteSuppression() {
  const qc = useQueryClient();
  return useMutation<void, unknown, SuppressionTarget>({
    meta: { suppressErrorToast: true },
    mutationFn: ({ channel, address, sender }) =>
      apiDelete<void>(
        `/suppressions/${channel}/${encodeURIComponent(address)}` +
          (sender !== null ? `?sender=${encodeURIComponent(sender)}` : ""),
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["suppressions"] }),
  });
}
